import { useState, useMemo, useEffect } from 'react';
import { styled } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import DashboardNavbar from './DashboardNavbar';
import DashboardSidebar from './DashboardSidebar';
import { useNotification } from 'src/context/notification-context';
import { TopNotificationAlert } from 'src/components/application/TopNotificationAlert';
import { getMobileOperatingSystem, getScreenSize } from 'src/utils/getUtils';

const DashboardLayoutRoot = styled('div')(() => ({
  backgroundColor: '#f2f4f6',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  overflow: 'hidden',
}));

const NotificationContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  top: 0,
  left: 0,
  width: '100%',
  zIndex: theme.zIndex.appBar,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left',
  [theme.breakpoints.down('md')]: {
    maxHeight: '290px',
    overflowY: 'auto',
  },
}));

const DashboardLayoutWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden',
  transition: 'padding-top 0.3s ease',
  [theme.breakpoints.up('lg')]: {
    paddingLeft: 256,
  },
  'body.dashboard-fullscreen &': {
    [theme.breakpoints.up('lg')]: {
      paddingLeft: 0,
    },
    paddingLeft: 0,
  },
}));

const DashboardLayoutContainer = styled('div')({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden',
});

const DashboardLayoutContent = styled('div')({
  flex: '1 1 auto',
  overflow: 'auto',
});

const DashboardLayout = () => {
  const location = useLocation();
  const [isMobileNavOpen, setMobileNavOpen] = useState(false);
  const [isMobileButtonShow, setIsMobileButtonShow] = useState(false);
  const { notifications, notificationRef, notificationHeight } = useNotification();
  const mobileButtonBarHeight = getMobileOperatingSystem() === 'unknown' ? 0 : 70;
  const [contentHeight, setContentHeight] = useState(getScreenSize().height - notificationHeight);

  function recalcContentHeight() {
    setIsMobileButtonShow(true);
    setContentHeight(getScreenSize().height - notificationHeight - mobileButtonBarHeight);
  }

  useEffect(() => {
    window.recalculateMainContentHeight = recalcContentHeight;
    window.addEventListener('resize', recalcContentHeight);
    return () => {
      window.removeEventListener('resize', recalcContentHeight);
    };
  });

  useEffect(() => {
    setIsMobileButtonShow(false);
    setContentHeight(getScreenSize().height - notificationHeight);
  }, [location]);

  useEffect(() => {
    setContentHeight(getScreenSize().height - notificationHeight - (isMobileButtonShow ? mobileButtonBarHeight : 0));
  }, [notificationHeight]);

  const hasVisibleNotifications = useMemo(() => notifications.some((n) => !n.hidden), [notifications]);

  return (
    <DashboardLayoutRoot>
      {hasVisibleNotifications && (
        <NotificationContainer ref={notificationRef}>
          {notifications
            .filter((n) => !n.hidden)
            .map((n) => (
              <TopNotificationAlert
                key={n.id}
                notification={n}
              />
            ))}
        </NotificationContainer>
      )}
      <DashboardNavbar onMobileNavOpen={() => setMobileNavOpen(true)} />
      <DashboardSidebar
        onMobileClose={() => setMobileNavOpen(false)}
        openMobile={isMobileNavOpen}
      />
      <DashboardLayoutWrapper>
        <DashboardLayoutContainer className='main-container'>
          <DashboardLayoutContent sx={contentHeight != null ? { height: contentHeight } : {}}>
            <Outlet />
          </DashboardLayoutContent>
        </DashboardLayoutContainer>
      </DashboardLayoutWrapper>
    </DashboardLayoutRoot>
  );
};

export default DashboardLayout;
